import React from 'react'
import Icons from '../../../components/Icons'
import styled from 'styled-components'
import Button from '../../../components/Button/Button'
import {
  formatDate,
  formatTime,
  combineDateAndTimeStrings
} from '../../../utils/time'
import {isCheckIn, isCheckOut} from '../../../utils/meeting'
import {_L, useLocaleState} from '../../../context/LocaleContext'

const EntireRow = styled.div`
  border-bottom: 1px solid #f3f5fb;
  padding: 8px 24px 17px 24px;
  cursor: pointer;
  &:hover {
    background-color: #f3f9ff;
  }
`

const Row = styled.div`
  display: flex;
  box-sizing: border-box;
  justify-content: flex-start;
  flex-wrap: wrap;
  align-items: center;
`

const DateValue = styled.div`
  font-size: 43px;
  font-weight: 600;

  @media (max-width: 1024px) {
    font-size: 41px;
    font-weight: 600;
  }
`

const DayValue = styled.div`
  font-size: 12px;
  font-weight: 600;
  vertical-align: top;
  color: #a4a9b7;
  margin: 5px;
`

const DateContainer = styled.div`
  display: flex;
  min-width: 150px;

  @media (max-width: 1600px) {
    min-width: 90px;
  }

  @media (max-width: 420px) {
    min-width: 100%;
  }
`

const MeetingDate = ({date, day}) => (
  <DateContainer>
    <DateValue>{date}</DateValue>
    <DayValue>{day}</DayValue>
  </DateContainer>
)

const TimeLabel = styled.div`
  font-size: 12px;
  color: #a4a9b7;

  @media (max-width: 420px) {
    padding-top: 20px;
  }
`

const TimeValue = styled(TimeLabel)`
  font-size: 16px;
  color: #000000;

  @media (max-width: 420px) {
    padding-top: 2px;
  }
`

const TimeContainer = styled.div`
  margin-left: auto;
  width: 90px;
  margin-right: ${props => (props.endTime ? 0 : '6px')};

  @media (max-width: 420px) {
    width: 100px;
    margin-right: ${props => (props.endTime ? '10px' : '16px')};
  }
`

// TODO: Separate file?
const Time = ({label, time}) => (
  <TimeContainer>
    <TimeLabel>{label}</TimeLabel>
    <TimeValue>{time}</TimeValue>
  </TimeContainer>
)

const Reservation = styled.div`
  display: inline-block;
  font-size: 16px;
  font-weight: 600;
  margin-right: 8px;
  max-width: calc(100% - 20px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Location = styled.div`
  font-size: 14px;
  color: #a4a9b7;
`

const ReservationContainer = styled.div`
  flex: 1;
  width: calc(100% - 400px);

  @media (max-width: 420px) {
    min-width: 100%;
  }
`

const ButtonRow = styled.div`
  display: ${props => (props.isCheckIn ? 'flex' : 'none')};
  justify-content: flex-end;
  margin-top: 4px;
  margin-right: 20px;
`

const CheckInButton = styled(Button)`
  background: #3395fe;
  color: #ffffff;
  height: 46px;
  width: 174px;
  font-size: 16px;
  font-weight: 400;
  padding-top: 0;
  text-align: center;
  border-radius: 6px;
`

const CheckOutButton = styled(Button)`
  height: 46px;
  border-radius: 6px;
  border: solid 2px #3395fe;
  background: #fff;
  color: #3395fe !important;
  font-weight: 600;
  padding-top: 0;
  text-align: center;
  width: 174px;
`

const MeetingReservation = ({onCheckIn, onCheckOut, meeting, onClick}) => {
  const localeState = useLocaleState()

  const doCheckIn = () => {
    onCheckIn(meeting)
  }

  const doCheckOut = () => {
    onCheckOut(meeting)
  }

  const onRowClick = () => {
    onClick(meeting)
  }

  const {
    reserveDateStart,
    reserveTimeStart,
    rmName,
    rmId,
    flId,
    reserveDateEnd,
    reserveTimeEnd,
    reserveReservationName,
    reserveResType,
    verified
  } = meeting

  const isCheckedIn = verified === 1

  const meetingStart = combineDateAndTimeStrings(
    reserveDateStart,
    reserveTimeStart
  )

  const meetingEnd = combineDateAndTimeStrings(reserveDateEnd, reserveTimeEnd)
  const checkIn = isCheckIn({isCheckedIn, meetingStart, meetingEnd})
  const checkOut = isCheckOut({isCheckedIn, meetingStart, meetingEnd})

  return (
    <EntireRow>
      <Row onClick={onRowClick}>
        <MeetingDate
          date={formatDate(reserveDateStart, 'D')}
          day={formatDate(reserveDateStart, 'ddd')}
        />
        <ReservationContainer>
          <Reservation>{reserveReservationName}</Reservation>

          {reserveResType === 'recurring' && (
            <span>
              <Icons icon="RECURRENCE" />
            </span>
          )}
          <Location>
            {rmName} {rmId} - {_L({key: 'Floor', localeState})} {flId}
          </Location>
        </ReservationContainer>
        <Time
          label={_L({key: 'Time Start:', localeState})}
          time={formatTime(reserveTimeStart, 'LT')}
        />
        <Time
          label={_L({key: 'Time End:', localeState})}
          time={formatTime(reserveTimeEnd, 'LT')}
          endTime
        />
      </Row>

      <ButtonRow isCheckIn={checkIn}>
        <CheckInButton
          label={_L({key: 'Check-In', localeState})}
          onClick={doCheckIn}
        />
      </ButtonRow>
      <ButtonRow isCheckIn={checkOut}>
        <CheckOutButton
          onClick={doCheckOut}
          label={_L({key: 'Check-Out', localeState})}
        />
      </ButtonRow>
    </EntireRow>
  )
}

export default MeetingReservation
